import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MessageSquare, CreditCard, TrendingUp, Calendar, ChevronDown, ChevronUp } from 'lucide-react';
import { studentApi, chatApi } from '../services/api';
import { useAuthStore } from '../store/authStore';
import toast from 'react-hot-toast';

function StudentDashboard() {
  const { user } = useAuthStore();
  const [quota, setQuota] = useState(null);
  const [usage, setUsage] = useState(null);
  const [progress, setProgress] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedCard, setExpandedCard] = useState(null);
  const [drillDownData, setDrillDownData] = useState(null);
  const [drillDownLoading, setDrillDownLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const handleCardClick = async (cardId) => {
    if (expandedCard === cardId) { setExpandedCard(null); return; }
    setExpandedCard(cardId);
    setDrillDownLoading(true);
    try {
      let res;
      switch (cardId) {
        case 'questions':
          res = await studentApi.getRechargeHistory();
          break;
        case 'totalAsked':
          res = await studentApi.getDailyUsage(30);
          break;
        case 'streak':
          res = await studentApi.getActivityCalendar(30);
          break;
        case 'conversations':
          res = await chatApi.getConversations(10, 0);
          break;
      }
      setDrillDownData(res.data);
    } catch (e) {
      toast.error('Failed to load details');
    } finally {
      setDrillDownLoading(false);
    }
  };

  const loadData = async () => {
    try {
      const [quotaRes, usageRes, progressRes] = await Promise.all([
        studentApi.getQuota(),
        studentApi.getUsage(),
        studentApi.getProgress()
      ]);
      setQuota(quotaRes.data);
      setUsage(usageRes.data);
      setProgress(progressRes.data);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Welcome, {user?.name}</h1>
        {user?.trainerName && (
          <p className="text-sm text-gray-500">Trainer: {user.trainerName}</p>
        )}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        {/* Questions Available */}
        <div
          onClick={() => handleCardClick('questions')}
          className={`bg-white rounded-xl p-6 border cursor-pointer transition-colors ${
            expandedCard === 'questions' ? 'border-primary-400 ring-1 ring-primary-200' : 'border-gray-200 hover:border-primary-300'
          }`}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="p-2 bg-primary-100 rounded-lg">
              <MessageSquare className="w-6 h-6 text-primary-600" />
            </div>
            <div className="flex items-center space-x-1">
              {quota?.tier === 'L1' && (
                <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full">Trial</span>
              )}
              {expandedCard === 'questions' ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
            </div>
          </div>
          <p className="text-3xl font-bold text-gray-900">
            {quota?.tier === 'L1' ? (quota.totalLimit ? quota.questionsRemaining : quota.questionsRemainingToday) : quota?.availableQuestions || 0}
          </p>
          <p className="text-sm text-gray-500">
            {quota?.tier === 'L1' ? (quota.totalLimit ? 'Questions remaining' : 'Questions left today') : 'Questions available'}
          </p>
          {quota?.tier !== 'L1' && quota?.nextResetDate && (
            <p className="text-xs text-gray-400 mt-1">
              Resets on: {new Date(quota.nextResetDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
            </p>
          )}
          {quota?.availableQuestions < 20 && quota?.tier !== 'L1' && (
            <Link to="/recharge" className="text-sm text-primary-600 hover:underline mt-2 inline-block" onClick={(e) => e.stopPropagation()}>
              Recharge now
            </Link>
          )}
        </div>

        {/* Total Questions Asked */}
        <div
          onClick={() => handleCardClick('totalAsked')}
          className={`bg-white rounded-xl p-6 border cursor-pointer transition-colors ${
            expandedCard === 'totalAsked' ? 'border-green-400 ring-1 ring-green-200' : 'border-gray-200 hover:border-green-300'
          }`}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="p-2 bg-green-100 rounded-lg">
              <TrendingUp className="w-6 h-6 text-green-600" />
            </div>
            {expandedCard === 'totalAsked' ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
          </div>
          <p className="text-3xl font-bold text-gray-900">{progress?.totalQuestions || 0}</p>
          <p className="text-sm text-gray-500">Total questions asked</p>
        </div>

        {/* Current Streak */}
        <div
          onClick={() => handleCardClick('streak')}
          className={`bg-white rounded-xl p-6 border cursor-pointer transition-colors ${
            expandedCard === 'streak' ? 'border-orange-400 ring-1 ring-orange-200' : 'border-gray-200 hover:border-orange-300'
          }`}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Calendar className="w-6 h-6 text-orange-600" />
            </div>
            {expandedCard === 'streak' ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
          </div>
          <p className="text-3xl font-bold text-gray-900">{progress?.currentStreak || 0}</p>
          <p className="text-sm text-gray-500">Day streak</p>
        </div>

        {/* Conversations */}
        <div
          onClick={() => handleCardClick('conversations')}
          className={`bg-white rounded-xl p-6 border cursor-pointer transition-colors ${
            expandedCard === 'conversations' ? 'border-purple-400 ring-1 ring-purple-200' : 'border-gray-200 hover:border-purple-300'
          }`}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="p-2 bg-purple-100 rounded-lg">
              <CreditCard className="w-6 h-6 text-purple-600" />
            </div>
            {expandedCard === 'conversations' ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
          </div>
          <p className="text-3xl font-bold text-gray-900">{progress?.conversationCount || 0}</p>
          <p className="text-sm text-gray-500">Conversations</p>
        </div>
      </div>

      {/* Drill-Down Panel */}
      {expandedCard && (
        <div className="mb-8 bg-white rounded-xl border border-gray-200 overflow-hidden">
          {drillDownLoading ? (
            <div className="p-6 text-center text-gray-500">Loading...</div>
          ) : expandedCard === 'questions' && Array.isArray(drillDownData) ? (
            <div>
              <div className="p-4 border-b bg-gray-50">
                <h3 className="font-semibold text-gray-700">Recent Recharges</h3>
              </div>
              {drillDownData.length > 0 ? (
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Package</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Questions</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {drillDownData.map((item, i) => (
                      <tr key={i}>
                        <td className="px-4 py-2 text-sm text-gray-900">{item.package_name}</td>
                        <td className="px-4 py-2 text-sm text-gray-600">₹{item.amount}</td>
                        <td className="px-4 py-2 text-sm text-gray-600">{item.questions_granted}</td>
                        <td className="px-4 py-2 text-sm text-gray-500">{new Date(item.created_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="p-6 text-center text-gray-500">No recharges yet</div>
              )}
            </div>
          ) : expandedCard === 'totalAsked' && Array.isArray(drillDownData) ? (
            <div>
              <div className="p-4 border-b bg-gray-50">
                <h3 className="font-semibold text-gray-700">Daily Usage (Last 30 Days)</h3>
              </div>
              {drillDownData.length > 0 ? (
                <div className="p-4">
                  <div className="flex items-end space-x-1 h-32">
                    {drillDownData.slice().reverse().map((day, i) => {
                      const max = Math.max(...drillDownData.map(d => d.questions_asked), 1);
                      const height = (day.questions_asked / max) * 100;
                      return (
                        <div key={i} className="flex-1 flex flex-col items-center" title={`${day.date}: ${day.questions_asked} questions`}>
                          <div
                            className="w-full bg-green-400 rounded-t min-h-[2px]"
                            style={{ height: `${Math.max(height, 2)}%` }}
                          />
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex justify-between text-xs text-gray-400 mt-1">
                    <span>{drillDownData.length > 0 ? new Date(drillDownData[drillDownData.length - 1].date).toLocaleDateString() : ''}</span>
                    <span>Today</span>
                  </div>
                </div>
              ) : (
                <div className="p-6 text-center text-gray-500">No usage data yet</div>
              )}
            </div>
          ) : expandedCard === 'streak' && Array.isArray(drillDownData) ? (
            <div>
              <div className="p-4 border-b bg-gray-50">
                <h3 className="font-semibold text-gray-700">Activity Calendar (Last 30 Days)</h3>
              </div>
              <div className="p-4">
                <div className="grid grid-cols-7 gap-1">
                  {(() => {
                    const activeDates = new Set(drillDownData.map(d => d.date?.split('T')[0]));
                    const days = [];
                    for (let i = 29; i >= 0; i--) {
                      const d = new Date();
                      d.setDate(d.getDate() - i);
                      const dateStr = d.toISOString().split('T')[0];
                      days.push(dateStr);
                    }
                    return days.map(dateStr => (
                      <div
                        key={dateStr}
                        className={`w-full aspect-square rounded-sm ${
                          activeDates.has(dateStr) ? 'bg-green-400' : 'bg-gray-100'
                        }`}
                        title={`${dateStr}${activeDates.has(dateStr) ? ' (active)' : ''}`}
                      />
                    ));
                  })()}
                </div>
                <div className="flex items-center space-x-2 mt-2 text-xs text-gray-500">
                  <div className="w-3 h-3 bg-gray-100 rounded-sm" /> <span>Inactive</span>
                  <div className="w-3 h-3 bg-green-400 rounded-sm" /> <span>Active</span>
                </div>
              </div>
            </div>
          ) : expandedCard === 'conversations' && drillDownData ? (
            <div>
              <div className="p-4 border-b bg-gray-50">
                <h3 className="font-semibold text-gray-700">Recent Conversations</h3>
              </div>
              {(drillDownData.conversations || drillDownData)?.length > 0 ? (
                <div className="divide-y divide-gray-200">
                  {(drillDownData.conversations || drillDownData).map((conv) => (
                    <Link
                      key={conv.id}
                      to={`/chat`}
                      className="block px-4 py-3 hover:bg-gray-50 transition-colors"
                    >
                      <p className="text-sm font-medium text-gray-900 truncate">{conv.title || 'Untitled'}</p>
                      <p className="text-xs text-gray-500">{new Date(conv.created_at || conv.updated_at).toLocaleDateString()}</p>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="p-6 text-center text-gray-500">No conversations yet</div>
              )}
            </div>
          ) : (
            <div className="p-6 text-center text-gray-500">No data available</div>
          )}
        </div>
      )}

      {/* Trial Info */}
      {quota?.tier === 'L1' && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
          <h3 className="font-semibold text-amber-900 mb-2">Free Trial Active</h3>
          <p className="text-amber-700 mb-4">
            You have {quota.daysRemaining} days remaining in your trial.
            {quota.totalLimit ? `Total limit: ${quota.totalLimit} questions.` : `Daily limit: ${quota.dailyLimit} questions per day.`}
          </p>
          <Link
            to="/recharge"
            className="inline-block px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700"
          >
            Upgrade to Full Access
          </Link>
        </div>
      )}

      {/* Usage History */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="p-4 border-b">
          <h2 className="text-lg font-semibold">Usage History</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Month</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Questions</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Recharges</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Spent</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {usage?.monthly?.length > 0 ? (
                usage.monthly.map((month) => (
                  <tr key={month.year_month}>
                    <td className="px-4 py-3 text-sm text-gray-900">{month.year_month}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{month.questions_asked}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{month.recharges_bought}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">₹{month.amount_spent}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="px-4 py-8 text-center text-gray-500">
                    No usage history yet. Start chatting to see your stats!
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default StudentDashboard;
